import React, { useState, useRef, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { apiService } from '@/services/api';
import { OTP_ENABLED, PhoneVerify } from './PhoneOtpGate';
import { underHandoff } from '@/lib/handoff';

const BasicContactStep = ({ question, value, onAnswer, onNext, setLoading, minAge = 18 }) => {
  const [formData, setFormData] = useState({
    name: value?.name || '',
    email: value?.email || '',
    phone: value?.phone || '',
    dob: value?.dob || ''
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const containerRef = useRef(null);
  const [errors, setErrors] = useState({});
  // §otp: phone verification lives right here under the phone field (all visit
  // types + tenants). The token rides the basic-contact answer to checkout.
  // On resume (value has a token) treat the saved phone as already verified.
  const [phoneVerified, setPhoneVerified] = useState(!!value?.phoneVerifyToken);
  const [phoneVerifyToken, setPhoneVerifyToken] = useState(value?.phoneVerifyToken || null);
  const [verifiedPhone, setVerifiedPhone] = useState(value?.phoneVerifyToken ? (value?.phone || '') : '');

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      newErrors.name = 'Please enter your full name';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone.trim() || formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Please enter a valid phone number';
    } else if (OTP_ENABLED && !phoneVerified) {
      // §otp hard-gate: a valid number isn't enough — it must be SMS-verified.
      newErrors.phone = 'Please verify your phone number to continue';
    }
    // Date of birth validation (MM/DD/YYYY): required, valid, not future, 18+ years old
    if (!formData.dob) {
      newErrors.dob = 'Date of birth is required';
    } else {
      const m = formData.dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!m) {
        newErrors.dob = 'Use MM/DD/YYYY format';
      } else {
        const month = parseInt(m[1], 10);
        const day = parseInt(m[2], 10);
        const year = parseInt(m[3], 10);
        const now = new Date();
        // Basic range checks
        if (year < 1900 || year > now.getFullYear()) {
          newErrors.dob = 'Please enter a valid year';
        } else if (month < 1 || month > 12) {
          newErrors.dob = 'Please enter a valid month';
        } else {
          // Check days per month, account for leap year
          const daysInMonth = new Date(year, month, 0).getDate();
          if (day < 1 || day > daysInMonth) {
            newErrors.dob = 'Please enter a valid day';
          } else {
            const dobDate = new Date(year, month - 1, day);
            if (dobDate > now) {
              newErrors.dob = 'Date of birth cannot be in the future';
            } else {
              // Minimum-age gate (default 18; sermorelin/tesamorelin pass 20
              // per Beluga policy — see docs/BACKEND_GH_THERAPY_AGE20_PRD.md).
              const minAgeCutoff = new Date(now.getFullYear() - minAge, now.getMonth(), now.getDate());
              if (dobDate > minAgeCutoff) {
                newErrors.dob = `You must be at least ${minAge} years old`;
              }
            }
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // First validate the form
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    // §authenticated-patient handoff: a patient who arrived with a ?pt= token IS
    // a known account — this step only renders for them when the handoff was
    // incomplete (e.g. no dob on file) or didn't resolve, so they can fill the
    // gap. The "email already exists" gate must NOT block them (that's the very
    // bug the handoff fixes), so skip the existence check entirely whenever a
    // (not-proven-invalid) token is present — see underHandoff() on why this is
    // the weaker check and why that's correct here.
    if (!underHandoff()) {
      try {
        // Try to check if user exists, but don't block form submission on failure
        const res = await apiService.getUserExists({email: formData.email});
        console.log('exists response:', res);

        if (res && res.user_count !== 0) {
          setLoading(false);
          setErrors({email: 'An account with this email already exists'});
          return;
        }
      } catch (err) {
        // Log the error but continue - don't block form submission if API is down
        console.warn('Error checking user existence (continuing anyway):', err.message);
      }
    }

    setLoading(false);
    // §otp: persist the phone-verify token on the answer so checkout can relay
    // it to the payment (undefined when the flag is off — no behavior change).
    onAnswer({ ...formData, phoneVerifyToken: phoneVerifyToken || undefined });
    onNext();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // §otp: editing the phone after verifying invalidates it — the token is
    // HMAC-bound to the exact number, so a changed number must re-verify.
    if (field === 'phone' && phoneVerified && value !== verifiedPhone) {
      setPhoneVerified(false);
      setPhoneVerifyToken(null);
      setVerifiedPhone('');
    }
  };

  // §otp: called by PhoneVerify once /otp/verify returns verified=true.
  const handlePhoneVerified = (token) => {
    setPhoneVerifyToken(token);
    setPhoneVerified(true);
    setVerifiedPhone(formData.phone);
    setErrors(prev => ({ ...prev, phone: '' }));
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const formatDateToMMDDYYYY = (date) => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div data-testid={`step-${question.id}`}>
      {/* Question */}
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        {question.question}
      </h2>

      {/* Subtitle */}
      {question.subtitle && (
        <p className="text-center text-gray-500 mb-8">
          {question.subtitle}
        </p>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="John Doe"
            className={`form-input ${errors.name ? 'error' : ''}`}
            data-testid="name-input"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="john@example.com"
            className={`form-input ${errors.email ? 'error' : ''}`}
            data-testid="email-input"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', formatPhone(e.target.value))}
            placeholder="(555) 123-4567"
            className={`form-input ${errors.phone ? 'error' : ''}`}
            data-testid="phone-input"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          {/* §otp: SMS verification, dark unless REACT_APP_OTP_ENABLED. Shows only
              once a complete 10-digit number is entered. */}
          <PhoneVerify
            phone={formData.phone.replace(/\D/g, '').length >= 10 ? formData.phone : ''}
            verified={phoneVerified}
            onVerified={handlePhoneVerified}
          />
        </div>

        {/* Date of Birth (MM/DD/YYYY) with calendar picker */}
        <div ref={containerRef} className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={formData.dob}
              onChange={(e) => {
                // Allow typing and keep MMDDYYYY digits only formatting
                const raw = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                let formatted = raw;
                if (raw.length >= 3 && raw.length <= 4) {
                  formatted = raw.slice(0, 2) + '/' + raw.slice(2);
                } else if (raw.length > 4) {
                  formatted = raw.slice(0, 2) + '/' + raw.slice(2, 4) + '/' + raw.slice(4);
                }
                handleChange('dob', formatted);
              }}
              placeholder="MM/DD/YYYY"
              className={`form-input ${errors.dob ? 'error' : ''}`}
              data-testid="dob-input"
              onFocus={() => setShowCalendar(true)}
            />
            <button
              type="button"
              onClick={() => setShowCalendar(prev => !prev)}
              className="p-2 bg-gray-100 rounded-md"
              aria-label="Toggle calendar"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
          {errors.dob && <p className="text-red-500 text-xs mt-1">{errors.dob}</p>}

          {showCalendar && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="relative bg-white shadow-lg rounded-md p-4 mx-4 max-w-md w-full">
                <DayPicker
                  mode="single"
                  selected={formData.dob ? new Date(formData.dob.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$3-$1-$2')) : undefined}
                  onSelect={(date) => {
                    if (!date) return;
                    const formatted = formatDateToMMDDYYYY(date);
                    handleChange('dob', formatted);
                    setShowCalendar(false);
                  }}
                  fromYear={1900}
                  toYear={new Date().getFullYear()}
                  captionLayout="dropdown-buttons"
                />
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={() => setShowCalendar(false)}
                    className="py-2 px-4 bg-gray-100 rounded-md text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          className="btn-primary w-full mt-6"
          data-testid="continue-button"
        >
          Continue
        </button>
      </form>
    </div>
  );
};

export default BasicContactStep;